// src/pages/Admin/Vendors/VendorSettings.tsx
import React, { useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { Settings, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { adminAPI } from '../../../api/admin';
import showToast from '../../../utils/toast';

interface VendorDetail {
  id: string;
  business_name: string;
  commission_rate: number;
  is_active: boolean;
  verification_status: string;
}

const VendorSettings: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { vendor, refresh } = useOutletContext<{ vendor: VendorDetail; refresh: () => void }>();
  const [formData, setFormData] = useState({
    commission_rate: vendor?.commission_rate || 10.00,
    is_active: vendor?.is_active ?? true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAPI.updateVendor(id!, {
        commission_rate: formData.commission_rate,
        is_active: formData.is_active,
      });
      showToast('Vendor settings updated successfully', 'success');
      refresh?.();
    } catch (error) {
      showToast('Failed to update vendor settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="h-5 w-5 text-gray-600" />
        <h2 className="text-lg font-semibold text-gray-900">Vendor Settings</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Commission Rate */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Commission Rate (%)
          </label>
          <div className="relative max-w-xs">
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.commission_rate}
              onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Percentage taken from each sale</p>
        </div>

        {/* Account Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Account Status
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={formData.is_active === true}
                onChange={() => setFormData({ ...formData, is_active: true })}
                className="h-4 w-4 text-blue-600"
              />
              <span className="text-sm text-green-600">Active</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={formData.is_active === false}
                onChange={() => setFormData({ ...formData, is_active: false })}
                className="h-4 w-4 text-red-600"
              />
              <span className="text-sm text-red-600">Suspended</span>
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1">Suspending will hide vendor's products from the store</p>
        </div>

        {/* Verification Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Verification Status
          </label>
          <div className="flex items-center gap-2">
            {vendor?.verification_status === 'approved' ? (
              <span className="inline-flex items-center gap-1 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" />
                Verified
              </span>
            ) : vendor?.verification_status === 'pending' ? (
              <span className="inline-flex items-center gap-1 text-sm text-yellow-600">
                <AlertCircle className="h-4 w-4" />
                Pending Review
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                Rejected
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">Vendor verification status can be changed in the application review</p>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
};

export default VendorSettings;